import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { xpApi } from "@/lib/experienceApi";
import { formatParisDate } from "@/lib/dtParis";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { participantAuth } from "@/lib/experienceApi";
import { Wine, Calendar, MapPin, QrCode } from "lucide-react";

export default function ExperienceHome() {
  const [events, setEvents] = useState([]);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    xpApi.publicEvents().then(setEvents).catch(() => setEvents([]));
    // Auto-redirect if already logged in
    if (participantAuth.getToken()) {
      nav("/experience/my");
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = code.trim().toUpperCase();
    if (!cleaned) return;
    setLoading(true);
    try {
      const res = await xpApi.participantLogin(cleaned);
      participantAuth.setToken(res.session_token);
      participantAuth.setCtx({ ticket: res.ticket, event_id: res.event_id });
      toast.success("Bienvenue !");
      nav("/experience/my");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code invalide");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] max-w-3xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E5276A]/10 text-[#E5276A] text-xs font-semibold uppercase tracking-wide mb-3">
          <QrCode className="w-3.5 h-3.5" /> geoVINUM Experience
        </div>
        <h1 className="text-4xl sm:text-5xl font-display text-[#1F3A5F] mb-2">Votre expérience commence ici</h1>
        <p className="text-neutral-600 text-sm sm:text-base">Entrez votre code participant pour rejoindre l'événement.</p>
      </div>

      <Card className="mb-8" data-testid="participant-login-card">
        <CardHeader><CardTitle className="text-lg">Rejoindre par code</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="flex gap-2 items-stretch">
            <Input
              data-testid="participant-code-input"
              placeholder="GV-XXXXXX"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="uppercase tracking-widest text-center text-lg font-mono"
              maxLength={12}
              autoFocus
            />
            <Button data-testid="participant-login-btn" type="submit" disabled={loading} className="bg-[#E5276A] hover:bg-[#C01661]">
              {loading ? "…" : "Entrer"}
            </Button>
          </form>
          <p className="text-xs text-neutral-500 mt-2">Le code est imprimé sur votre billet ou reçu par email.</p>
        </CardContent>
      </Card>

      <div className="mb-4">
        <h2 className="text-lg font-semibold text-neutral-800 mb-2">Événements à venir</h2>
      </div>
      <div className="grid gap-3">
        {events.length === 0 && (
          <p className="text-sm text-neutral-500">Aucun événement publié pour l'instant.</p>
        )}
        {events.map((ev) => (
          <Link key={ev.id} to={`/experience/event/${ev.slug}`} data-testid={`event-card-${ev.slug}`}>
            <Card className="hover:border-[#E5276A] transition">
              <CardContent className="p-4 flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg bg-[#E5276A]/10 flex items-center justify-center flex-shrink-0">
                  <Wine className="w-6 h-6 text-[#E5276A]" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-[#1F3A5F]">{ev.name}</div>
                  <div className="text-xs text-neutral-500 flex items-center gap-2 mt-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatParisDate(ev.starts_at)}
                    {ev.location?.address && <><MapPin className="w-3.5 h-3.5 ml-2" /> {ev.location.address}</>}
                  </div>
                  {ev.description && (
                    <div className="text-xs text-neutral-600 mt-1 line-clamp-2">{ev.description}</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
